import _ from 'lodash'
import React, { useContext, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import TextInput from '@/components/ui/inputs/TextInput'
import SpecialitiesAutoCompleteComponent, { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'
import { CreateConsultationStepperContext } from '../CreateConsultationStepper'

export type FormValues = {
  reason: string
}

type SelectSpecialityProps = {
  onChange?: (values: FormValues) => void
  reasons?: string[]
  selectedSpeciality: SpecialityAutocompleteOption | null
  specialities: SpecialityAutocompleteOption[]
  onInputChange?: (value: string) => void
  onSpecialityChange: (event: any, newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null, reason: string) => void
}

export function SelectSpecialityStep(props: SelectSpecialityProps) {
  const { onChange, reasons = [], selectedSpeciality, specialities, onInputChange, onSpecialityChange } = props
  const { t } = useTranslation()

  const reasonInputContainer = useRef<HTMLDivElement>(null)

  const [reason, setReason] = useState<string>('')

  const reasonOptions = useMemo(() => _.map(reasons, (reason) => ({ label: reason, value: reason })), [reasons])

  const { values } = useContext(CreateConsultationStepperContext)

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    switch (field) {
      case 'reason':
        setReason(value)
        onChange?.({ reason: value })
        break
      default:
        break
    }
  }

  const handleSpecialityChange = (
    event: any,
    newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null,
    reason: string
  ) => {
    onSpecialityChange(event, newValue, reason)
  }

  const handleInputChange = (value: string) => {
    onInputChange?.(value)
  }

  return (
    <Container maxWidth="sm">
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="h1" sx={{ fontSize: '30px', fontWeight: 700 }}>
            {t('page.createConsultation.step.specialitySelection.title')}
          </Typography>
          {!values.medecin && (
            <Typography variant="subtitle1" sx={{ fontSize: '14px', fontWeigt: 400 }}>
              {t('page.createConsultation.step.specialitySelection.subtitle')}
            </Typography>
          )}
        </Stack>
        <Stack spacing={3}>
          <SpecialitiesAutoCompleteComponent
            id="speciality"
            name={'speciality'}
            label={t('field.speciality.label')}
            editable={true}
            canDelete={true}
            multiple={false}
            selectedSpecialities={selectedSpeciality}
            specialities={specialities}
            onInputChange={handleInputChange}
            onSpecialityChange={handleSpecialityChange}
          />

          <Box ref={reasonInputContainer}>
            <TextInput
              fullWidth
              InputProps={{
                endAdornment: !reasonOptions.length ? null : (
                  <PopupState variant="popover" popupId="reason-options">
                    {(popupState) => (
                      <>
                        <IconButton size="small" {...bindTrigger(popupState)}>
                          <KeyboardArrowDownIcon />
                        </IconButton>
                        <Menu
                          {...bindMenu(popupState)}
                          disablePortal
                          anchorReference="anchorPosition"
                          anchorPosition={{
                            top: reasonInputContainer.current?.getBoundingClientRect().bottom || 0,
                            left: reasonInputContainer.current?.getBoundingClientRect().left || 0,
                          }}
                          PaperProps={{
                            sx: [
                              reasonInputContainer.current && {
                                minWidth: reasonInputContainer.current.getBoundingClientRect().width,
                              },
                            ],
                          }}
                        >
                          {_.map(reasonOptions, (option, index) => (
                            <MenuItem
                              key={`${option}-${index}`}
                              onClick={() => {
                                setReason(option.value)
                                onChange?.({ reason: option.value })
                                popupState.close()
                              }}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </PopupState>
                ),
              }}
              label={t('field.reason.label', { context: 'consultation' })}
              name="reason"
              id="reason"
              onChange={handleChange('reason')}
              value={reason}
            />
          </Box>
        </Stack>
      </Stack>
    </Container>
  )
}

export default SelectSpecialityStep
